import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api';
import { LayoutDashboard, FileText, Building2, HardHat, Wrench, Receipt, LogOut, Menu, X, Bot, Sparkles, Bell, Check, ExternalLink, MessageCircle, Stamp, Users, BarChart3, Rocket, Crown, Clock } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { toast } from 'sonner';

const NAV_ITEMS = [
    { to: '/app/dashboard', labelKey: 'sidebar.dashboard', icon: LayoutDashboard, testid: 'sidebar-dashboard' },
    { to: '/app/preventivi', labelKey: 'sidebar.preventivi', icon: FileText, testid: 'sidebar-preventivi' },
    { to: '/app/sal', labelKey: 'sidebar.sal', icon: Building2, testid: 'sidebar-sal' },
    { to: '/app/fatture', labelKey: 'sidebar.fatture', icon: Receipt, testid: 'sidebar-fatture' },
    { to: '/app/sicurezza', labelKey: 'sidebar.sicurezza', icon: HardHat, testid: 'sidebar-sicurezza' },
    // { to: '/app/subappalti', labelKey: 'sidebar.subappalti', icon: Wrench, testid: 'sidebar-subappalti' }, // Hidden per richiesta utente — riattivare quando si gestiranno i subappalti
    { to: '/app/agenti', labelKey: 'sidebar.agenti', icon: Sparkles, testid: 'sidebar-agenti', plus: true },
    // { to: '/app/lead-conversations', labelKey: 'sidebar.lead_conversations', icon: MessageCircle, testid: 'sidebar-lead-conversations', plus: true }, // Spostato dentro Agenti AI come agente "Conversazioni AI"
];

const ADMIN_NAV_ITEMS = [
    { to: '/app/admin/firma', labelKey: 'sidebar.admin_firma', icon: Stamp, testid: 'sidebar-admin-firma' },
    { to: '/app/admin/professionisti', labelKey: 'sidebar.admin_professionisti', icon: Users, testid: 'sidebar-admin-professionisti' },
    { to: '/app/admin/analytics', labelKey: 'sidebar.admin_analytics', icon: BarChart3, testid: 'sidebar-admin-analytics' },
];

// Plan ranking: which plan is "above" which, used to pick the upgrade target.
const UPGRADE_TARGET = {
    trial: { id: 'pmi', label: 'PMI' },
    artigiano: { id: 'pmi', label: 'PMI' },
    pmi: { id: 'impresa', label: 'IMPRESA AI' },
    impresa: null,
};

/**
 * Persistent sidebar CTA that surfaces the trial countdown + Upgrade button.
 * Hidden entirely for grandfathered users on the top plan.
 */
const UpgradeCTA = ({ status, onClickItem }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    if (!status) return null;
    const plan = (status.plan || 'trial').toLowerCase();
    const target = UPGRADE_TARGET[plan];
    const grandfathered = !!status.grandfathered;
    const isTrial = status.is_trialing && plan === 'trial';
    const trialExpired = !status.access_allowed && status.access_reason === 'trial_expired';
    if (grandfathered && !target) return null;

    const go = (planId) => {
        if (onClickItem) onClickItem();
        navigate(`/checkout?plan=${planId || target?.id || 'pmi'}`);
    };

    if (trialExpired) {
        return (
            <div className="mx-3 my-2 rounded-lg p-3 bg-[hsl(0_84%_55%)]/10 border border-[hsl(0_84%_55%)]/30" data-testid="sidebar-trial-expired">
                <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-[hsl(0_84%_70%)]" />
                    <span className="text-[11px] font-bold tracking-wider text-[hsl(0_84%_85%)]">{t('sidebar.trial_expired_badge')}</span>
                </div>
                <p className="text-xs text-white/75 leading-snug mb-2">{t('sidebar.trial_expired_body')}</p>
                <Button onClick={() => go('pmi')} className="w-full h-9 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold hover:bg-[hsl(45_100%_48%)]" data-testid="sidebar-upgrade-expired">
                    <Crown className="w-3.5 h-3.5 mr-1.5" /> {t('sidebar.choose_plan')}
                </Button>
            </div>
        );
    }

    if (isTrial) {
        const days = status.trial_days_remaining ?? 0;
        return (
            <div className="mx-3 my-2 rounded-lg p-3 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30" data-testid="sidebar-trial-banner">
                <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
                    <span className="text-[11px] font-bold tracking-wider text-[hsl(var(--accent))]">{t('sidebar.trial_active_badge')}</span>
                </div>
                <p className="text-xs text-white/80 leading-snug mb-2">
                    {days > 0
                        ? t('sidebar.trial_days_remaining', { count: days, days })
                        : t('sidebar.trial_last_day')}
                </p>
                <Button onClick={() => go('pmi')} className="w-full h-9 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold hover:bg-[hsl(45_100%_48%)]" data-testid="sidebar-upgrade-trial">
                    <Rocket className="w-3.5 h-3.5 mr-1.5" /> {t('sidebar.upgrade_now')}
                </Button>
            </div>
        );
    }

    if (target) {
        return (
            <div className="mx-3 my-2 rounded-lg p-3 bg-[hsl(220_10%_16%)] border border-[hsl(220_10%_22%)]" data-testid="sidebar-upgrade-banner">
                <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4 text-[hsl(var(--accent))]" />
                    <span className="text-[11px] font-bold tracking-wider text-white/70">{t('sidebar.upgrade_label')}</span>
                </div>
                <p className="text-xs text-white/70 leading-snug mb-2">{t('sidebar.upgrade_to', { plan: target.label })}</p>
                <Button onClick={() => go(target.id)} variant="outline" className="w-full h-9 bg-transparent border-[hsl(var(--accent))] text-[hsl(var(--accent))] font-semibold hover:bg-[hsl(var(--accent))] hover:text-[hsl(220_13%_10%)]" data-testid="sidebar-upgrade-cta">
                    {t('sidebar.upgrade_button', { plan: target.label })}
                </Button>
            </div>
        );
    }
    return null;
};


const SidebarContent = ({ onClickItem }) => {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    return (
        <div className="flex flex-col h-full bg-[hsl(220_13%_10%)] text-white">
            <div className="p-5 border-b border-[hsl(220_10%_22%)]">
                <Logo variant="light" />
            </div>
            <div className="px-3 py-3 border-b border-[hsl(220_10%_22%)]">
                <div className="text-xs tracking-widest text-white/50 mb-1 px-2">AZIENDA</div>
                <NavLink
                    to="/app/profilo"
                    onClick={onClickItem}
                    data-testid="sidebar-profilo-link"
                    className={({ isActive }) => `block px-2 py-1 rounded-md transition-colors ${isActive ? 'bg-[hsl(220_10%_16%)]' : 'hover:bg-[hsl(220_10%_16%)]'}`}
                    title={t('pages.profilo_title')}
                >
                    <div className="font-semibold text-sm truncate">{user?.company_name || user?.full_name}</div>
                    <div className="text-xs text-white/50 truncate">{user?.email}</div>
                </NavLink>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                <div className="text-xs tracking-widest text-white/50 mb-2 px-2">CASSETTA ATTREZZI</div>
                {NAV_ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onClickItem}
                        data-testid={item.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                isActive ? 'bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] border-l-4 border-l-[hsl(220_13%_10%)]' : 'hover:bg-[hsl(220_10%_16%)] text-white/85'
                            }`
                        }
                    >
                        <item.icon className="w-5 h-5" />
                        <span className="flex-1">{t(item.labelKey)}</span>
                        {item.plus && (
                            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">PLUS+</span>
                        )}
                    </NavLink>
                ))}
                {(user?.role === 'admin' || ['demo@cantiere.ai', 'alessandroscarpuzza@gmail.com'].includes((user?.email || '').toLowerCase())) && (
                    <>
                        <div className="text-xs tracking-widest text-white/50 mb-2 mt-5 px-2 flex items-center gap-2">
                            <Stamp className="w-3 h-3" /> ADMIN
                        </div>
                        {ADMIN_NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={onClickItem}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isActive ? 'bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] border-l-4 border-l-[hsl(220_13%_10%)]' : 'hover:bg-[hsl(220_10%_16%)] text-white/85'
                                    }`
                                }
                            >
                                <item.icon className="w-5 h-5" />
                                <span className="flex-1">{t(item.labelKey)}</span>
                            </NavLink>
                        ))}
                    </>
                )}
            </nav>
            <UpgradeCTA status={user?.status} onClickItem={onClickItem} />
            <div className="p-3 border-t border-[hsl(220_10%_22%)] space-y-2">
                <LanguageSwitcher variant="dark" />
                <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="w-full justify-start gap-2 text-white/85 hover:bg-[hsl(220_10%_16%)]" data-testid="sidebar-logout">
                    <LogOut className="w-4 h-4" /> {t('common.logout')}
                </Button>
            </div>
        </div>
    );
};

export default function AppShell() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { t } = useTranslation();
    const { logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    useEffect(() => {
        // No seed here - handled by Dashboard on first load
    }, []);

    return (
        <div className="min-h-screen flex bg-[hsl(0_0%_98%)]">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block w-[260px] flex-shrink-0 border-r border-[hsl(var(--border))]">
                <SidebarContent />
            </aside>
            {/* Mobile sidebar */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent side="left" className="p-0 w-[280px] bg-[hsl(220_13%_10%)]">
                    <SidebarContent onClickItem={() => setMobileOpen(false)} />
                </SheetContent>
            </Sheet>

            <main className="flex-1 flex flex-col min-w-0">
                <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[hsl(var(--border))] h-14 flex items-center px-4 gap-3">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
                        </SheetTrigger>
                    </Sheet>
                    <Logo />
                    <div className="ml-auto flex items-center gap-1">
                        <NotificationBell />
                        <Button
                            onClick={handleLogout}
                            variant="ghost"
                            size="sm"
                            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(0_70%_45%)] hover:bg-[hsl(0_70%_96%)]"
                            data-testid="header-logout-mobile"
                            aria-label={t('common.logout')}
                            title={t('common.logout')}
                        >
                            <LogOut className="w-5 h-5" />
                        </Button>
                    </div>
                </header>
                {/* Desktop top-right notification bell + logout */}
                <div className="hidden lg:flex justify-end items-center gap-2 px-8 pt-4 pb-0 -mb-2">
                    <NotificationBell />
                    <Button
                        onClick={handleLogout}
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(0_70%_45%)] hover:bg-[hsl(0_70%_96%)]"
                        data-testid="header-logout-desktop"
                    >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('common.logout')}</span>
                    </Button>
                </div>
                <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto pb-20 lg:pb-8">
                    <Outlet />
                </div>
                {/* Mobile bottom navigation */}
                <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[hsl(var(--border))] flex justify-around items-stretch h-16 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]" style={{ zIndex: 10000 }}>
                    {NAV_ITEMS.filter((it) => ['/app/dashboard', '/app/preventivi', '/app/sal', '/app/fatture', '/app/agenti'].includes(it.to)).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`bottom-nav-${item.to.split('/').pop()}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center flex-1 text-xs gap-1 transition-colors relative ${
                                    isActive ? 'text-[hsl(220_13%_10%)] bg-[hsl(var(--accent))]/10 border-t-2 border-t-[hsl(var(--accent))]' : 'text-[hsl(var(--muted-foreground))]'
                                }`
                            }
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="leading-none">{t(item.labelKey).split(' ')[0]}</span>
                            {item.plus && (
                                <span className="absolute top-1 right-2 text-[8px] font-bold tracking-wider px-1 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">+</span>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </main>
        </div>
    );
}


function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const res = await api.get('/notifications');
            setItems(res.data.items || []);
            setUnread(res.data.unread_count || 0);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000); // poll every 60s
        return () => clearInterval(t);
    }, []);

    const markRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            load();
        } catch (e) { /* ignore */ }
    };
    const markAllRead = async () => {
        try {
            await api.put('/notifications/read-all');
            toast.success('Tutte segnate come lette');
            load();
        } catch (e) { /* ignore */ }
    };
    const openItem = async (n) => {
        if (!n.read) await markRead(n.id);
        if (n.link) {
            setOpen(false);
            navigate(n.link);
        }
    };

    const kindStyle = (k) => ({
        success: 'border-l-[hsl(145_55%_50%)] bg-[hsl(145_45%_96%)]',
        warning: 'border-l-[hsl(45_90%_50%)] bg-[hsl(45_90%_96%)]',
        error: 'border-l-[hsl(0_70%_55%)] bg-[hsl(0_70%_96%)]',
        info: 'border-l-[hsl(210_80%_55%)] bg-white',
    }[k] || 'border-l-[hsl(210_80%_55%)] bg-white');

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
                    <Bell className="w-5 h-5" />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[hsl(0_70%_55%)] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
                            {unread > 9 ? '9+' : unread}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-0">
                <div className="flex items-center justify-between px-3 py-2.5 border-b">
                    <div className="font-display text-base">NOTIFICHE {unread > 0 && <span className="text-xs text-[hsl(var(--muted-foreground))] font-sans">({unread} da leggere)</span>}</div>
                    {unread > 0 && (
                        <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs gap-1 h-7" data-testid="notification-read-all">
                            <Check className="w-3 h-3" /> Segna tutte
                        </Button>
                    )}
                </div>
                <div className="max-h-[460px] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
                            <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
                            Nessuna notifica.
                        </div>
                    ) : items.slice(0, 30).map((n) => (
                        <button
                            key={n.id}
                            onClick={() => openItem(n)}
                            className={`w-full text-left px-3 py-2.5 border-l-4 ${kindStyle(n.kind)} ${n.read ? 'opacity-60' : ''} hover:bg-[hsl(var(--muted))]/40 transition-colors border-b border-[hsl(var(--border))]/40`}
                            data-testid={`notification-item-${n.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-sm">{n.title}</div>
                                {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(0_70%_55%)] mt-1.5 flex-shrink-0" />}
                            </div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{n.message}</div>
                            <div className="flex items-center justify-between mt-1">
                                <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{new Date(n.created_at).toLocaleString('it-IT')}</div>
                                {n.link && <ExternalLink className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />}
                            </div>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
